import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { LogoutAction } from '../enums/logout-action.enum';
import { CustomerFeConfig } from '../interfaces/environment.interface';
import { Session } from '../models/session.model';
import { User } from '../models/user.model';
import { ConfigService } from './config.service';
import { LoggerService } from './logger.service';
import { StorageService } from './storage.service';

/**
 * Session service
 */
@Injectable({
	providedIn: 'root'
})
export class SessionService {
	// =======================================================================================================================================
	// Properties
	// =======================================================================================================================================

	// Session id for server authentication
	protected session$: BehaviorSubject<Session>;

	// User, who is using the client
	protected currentUser$: BehaviorSubject<User>;

	// Current Captcha of User
	protected reCaptchaCode$: BehaviorSubject<string>;

	// Logout uri
	protected logoutUri$: BehaviorSubject<string>;

	// Id token
	protected idToken$: BehaviorSubject<string>;

	// Loaded configuration
	protected loadedConfig: CustomerFeConfig;

	// =======================================================================================================================================
	// Constructor
	// =======================================================================================================================================

	/**
	 * Constructor
	 *
	 * @param configService
	 * @param http
	 * @param loggerService
	 * @param router
	 * @param storageService
	 */
	constructor(
		protected configService: ConfigService,
		protected http: HttpClient,
		protected loggerService: LoggerService,
		protected router: Router,
		protected storageService: StorageService
	) {
		// Set loaded configuration
		this.configService.configObservable.subscribe((config) => {
			this.loadedConfig = config;
		});

		// Init session id
		const sessionData = storageService.getItem('session');
		this.session$ = sessionData ? new BehaviorSubject(Session.map(sessionData)) : new BehaviorSubject(null);

		// Init currentUser
		const userData = storageService.getItem('currentUser');
		this.currentUser$ = userData ? new BehaviorSubject(User.map(userData)) : new BehaviorSubject(null);

		// Init reCAPTCHA code
		this.reCaptchaCode$ = new BehaviorSubject<string>(null);

		// Init logout uri
		const logoutUri = storageService.getItem('logoutUri');
		this.logoutUri$ = logoutUri ? new BehaviorSubject(logoutUri) : new BehaviorSubject(null);

		// Init idToken
		const idTokenData = storageService.getItem('idToken');
		this.idToken$ = idTokenData ? new BehaviorSubject(idTokenData) : new BehaviorSubject(null);

		/*
		 * This should be done early, but for this to happen, the session and authentication service must be reworked.
		 */
		this.validateLoadedSession();
	}

	/**
	 * Validate a loaded session against the expiry date if it is still valid.
	 * This is to prevent access denied error for request when a timeout occurs.
	 * This prevents access denied errors, when a session times out.
	 * It is possible to add a this.refresh to try to restore the session before forcing the logout.
	 */
	private async validateLoadedSession(tryRefresh = false) {
		// verify object or localStorage
		if (!!this.session && this.session?.expirationDate && new Date(this.session?.expirationDate) < new Date()) {
			let refresh: Session = null;
			if (tryRefresh) {
				refresh = await this.refresh(false).toPromise();
			}

			if (!refresh && !tryRefresh) {
				this.clear(false); // Full clear will destroy required logoutUri
				this.check(true, true).toPromise();
			}
		}
	}

	// =======================================================================================================================================
	// Methods
	// =======================================================================================================================================

	/**
	 * Clear session
	 */
	public clear(full: boolean = false) {
		// Clear standard session data
		this.session = null;
		this.currentUser = null;
		this.reCaptchaCode = null;
		this.idToken = null;

		// Clear the non standard session data
		if (full) {
			this.logoutUri = null;
		}
	}

	/**
	 * Check if session is valid
	 *
	 * @param refresh Refresh if not valid
	 * @param redirect Redirect to logout if refreshing is not possible
	 */
	public check(refresh: boolean = true, redirect: boolean = true): Observable<boolean> {
		return new Observable((subscriber) => {
			// Session is invalid
			if (!this.session || !this.session.expirationDate || this.session.expirationDate < new Date()) {
				if (refresh) {
					if (this.router.url.includes('/register')) {
						this.refreshRegistrator().subscribe(
							() => {
								// Session valid
								subscriber.next(true);
								subscriber.complete();
							},
							(error1) => {
								// Session invalid
								subscriber.next(false);
								subscriber.complete();
							}
						);
					} else {
						this.refresh(redirect).subscribe(
							() => {
								// Session valid
								subscriber.next(true);
								subscriber.complete();
							},
							() => {
								// Session invalid
								subscriber.next(false);
								subscriber.complete();

								// Redirect
								if (redirect) {
									this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
								}
							}
						);
					}
				} else {
					// Session is invalid
					subscriber.next(false);
					subscriber.complete();

					// Redirect
					if (redirect) {
						this.router.navigate(['/user-auth/logout/' + LogoutAction.TIMEOUT]);
					}
				}
			} else {
				// Session is valid
				subscriber.next(true);
				subscriber.complete();
			}
		});
	}

	/**
	 * Refresh session
	 *
	 * @param redirect Redirect to logout if refreshing is not possible
	 */
	public refresh(redirect: boolean = true): Observable<Session> {
		return new Observable((subscriber) => {
			// Check refresh token
			if (!this.session || !this.session.refreshToken) {
				subscriber.error('Missing session');
				if (redirect) {
					this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
				}

				// Get session from server
			} else {
				let body = new FormData();
				body.append('refreshToken', this.session.refreshToken);
				this.http.post(this.loadedConfig.sessionRefreshUri + this.loadedConfig.clientId, body).subscribe(
					(session: any) => {
						// Check session
						if (!session || session.error) {
							if (!session) {
								session = { error: new Error('Error when renewing the session') };
							}
							this.loggerService.error(session.error);
							subscriber.error(session.error);
							if (redirect) {
								this.router.navigate(['/user-auth/logout/' + LogoutAction.TIMEOUT]);
							}
						} else {
							// Set session
							this.session = this.session ? this.session.map(session) : Session.map(session);

							// Return session
							subscriber.next(this.session);
							subscriber.complete();
						}
					},
					(err) => {
						// Error handling
						this.loggerService.error(err);
						subscriber.error(err);
						if (redirect) {
							this.router.navigate(['/user-auth/logout/' + LogoutAction.SECURITY]);
						}
					}
				);
			}
		});
	}

	public refreshRegistrator(): Observable<boolean> {
		return new Observable((subscriber) => {
			// Get session from authentication server
			this.http
				.post(this.loadedConfig.sessionCreationUri + this.loadedConfig.clientId + '/registrator', {
					ApplicationVersion: '1.0.0',
					MacAddress: '00:00:00:00:00:00'
				})
				.subscribe(
					(session) => {
						// Set session without user
						this.session = Session.map(session);
						this.currentUser = null;

						subscriber.next(true);
						subscriber.complete();
					},
					(err) => {
						this.loggerService.error(err);
						subscriber.error(err);
					}
				);
		});
	}

	// =======================================================================================================================================
	// Getter & Setter
	// =======================================================================================================================================

	/**
	 * Getter for currentUser object
	 *
	 * @returns
	 */
	public get currentUser(): User {
		return this.currentUser$.getValue();
	}

	/**
	 * Setter for currentUser object
	 *
	 * @param currentUser
	 */
	public set currentUser(currentUser: User) {
		if (currentUser !== null) {
			this.storageService.setItem('currentUser', currentUser);
			this.currentUser$.next(currentUser);
		} else {
			this.storageService.removeItem('currentUser');
			this.currentUser$.next(null);
		}
	}

	/**
	 * Getter for Observable of currentUser
	 *
	 * @returns
	 */
	public get currentUserObservable(): Observable<User> {
		return this.currentUser$.asObservable();
	}

	/**
	 * Getter for session object
	 *
	 * @returns
	 */
	public get session(): Session {
		return this.session$.getValue();
	}

	/**
	 * Setter for session object
	 *
	 * @param session
	 */
	public set session(session: Session) {
		if (session !== null) {
			this.storageService.setItem('session', session);
			this.session$.next(session);
		} else {
			this.storageService.removeItem('session');
			this.session$.next(null);
		}
	}

	/**
	 * Getter for Observable of session
	 *
	 * @returns
	 */
	public get sessionObservable(): Observable<Session> {
		return this.session$.asObservable();
	}

	/**
	 * Getter for captcha object
	 *
	 * @returns
	 */
	public get reCaptchaCode(): string {
		return this.reCaptchaCode$.value;
	}

	/**
	 * Setter for reCaptchaCode
	 *
	 * @param reCaptchaCode
	 */
	public set reCaptchaCode(reCaptchaCode: string) {
		if (reCaptchaCode !== null) {
			this.storageService.setItem('ReCaptchaCode', reCaptchaCode);
			this.reCaptchaCode$.next(reCaptchaCode);
		} else {
			this.storageService.removeItem('ReCaptchaCode');
			this.reCaptchaCode$.next(null);
		}
	}

	/**
	 * Getter for Observable of reCaptchaCode
	 *
	 * @returns
	 */
	public get reCaptchaCodeObservable(): Observable<string> {
		return this.reCaptchaCode$.asObservable();
	}

	/**
	 * Getter for logoutUri
	 */
	public get logoutUri(): string {
		return this.logoutUri$.getValue();
	}

	/**
	 * Setter for logoutUri
	 */
	public set logoutUri(logoutUri: string) {
		if (logoutUri !== null) {
			if (logoutUri !== this.logoutUri) {
				this.storageService.setItem('logoutUri', logoutUri);
				this.logoutUri$.next(logoutUri);
			}
		} else {
			this.storageService.removeItem('logoutUri');
			this.logoutUri$.next(null);
		}
	}

	/**
	 * Getter for Observable of logoutUri
	 */
	public get logoutUriObservable(): Observable<string> {
		return this.logoutUri$.asObservable();
	}

	/**
	 * Getter for idToken
	 */
	public get idToken(): string {
		return this.idToken$.getValue();
	}

	/**
	 * Setter for idToken
	 */
	public set idToken(idToken: string) {
		if (idToken !== null) {
			this.storageService.setItem('idToken', idToken);
			this.idToken$.next(idToken);
		} else {
			this.storageService.removeItem('idToken');
			this.idToken$.next(null);
		}
	}

	/**
	 * Getter for Observable of idToken
	 */
	public get idTokenObservable(): Observable<string> {
		return this.idToken$.asObservable();
	}
}
